import eventBus from 'widgets/toolbox/eventBus';
import { debounce } from 'widgets/toolbox/debounce';
import { get, getUrlParams } from 'widgets/toolbox/util';
import { RefElement } from 'widgets/toolbox/RefElement';

/**
 * @typedef {InstanceType<typeof import('widgets/toolbox/RefElement').RefElement>} refElement
 */

/* global dataLayer */

/**
 * @param {object[]} source
 * @param {number} [chunkSize=10]
 * @returns {object[][]}
 */
function arrayIntoChunks(source, chunkSize = 10) {
    return new Array(Math.ceil(source.length / chunkSize))
        .fill(0) // just make possible to call iteration on the next line
        .map((_, index) => source.slice(index * chunkSize, (index * chunkSize) + chunkSize));
}

/**
 * @description main method to push changes to datalayer
 * @param  {...any} args - data object
 */
function report(...args) {
    if (typeof dataLayer !== 'undefined') {
        dataLayer.push(...args);
    }
}

let currentCurrency = '';
const impressedProducts = [];

/**
 * @description Checks if product is already impressed
 * @param {string} id - product ID
 * @returns {boolean} result
 */
function hasBeenImpressed(id) {
    return impressedProducts.some(impressionId => impressionId === id);
}

/**
 * @description Sets tile position on the PLP\Search page
 * @param {refElement} tileInfo - product tile
 * @param {string} pid - product ID
 * @param {string} listId - id of list with products
 */
function setTilePosition(tileInfo, pid, listId) {
    pid = pid || tileInfo.id;
    listId = listId || 'productPositions';

    if (
        window[listId] && window[listId][pid]) {
        tileInfo.position = window[listId][pid];
    }
}

/**
 * @description Reports GTM impressions on page load
 */
function reportGtmImpressions() {
    if (window.gtmImpressions
        && window.gtmImpressions.ecommerce
        && window.gtmImpressions.ecommerce.impressions
    ) {
        window.gtmImpressions.ecommerce.impressions.forEach(impression => {
            if (window.productPositions && window.productPositions[impression.productID]) {
                impression.position = window.productPositions[impression.productID];

                if (window.location.search.indexOf('q=') > -1) {
                    impression.list = 'Search Results';
                }
            }

            impressedProducts.push(impression.productID);

            delete impression.productID;
        });

        dataLayer.push(window.gtmImpressions);
    }
}

/**
 * Transforms a cart item object by sanitizing and structuring its data.
 * 
 * This function takes an original cart item object, ensures all expected properties
 * are present (providing default values where necessary), and handles specific
 * product types (variant, bundle) with additional properties. It aims to prevent
 * runtime errors by safely accessing properties and providing fallbacks for missing data.
 *
 * @param {Object|null} originalItem - The original cart item object to be transformed.
 * If null or undefined, the function returns null.
 * @returns {Object|null} A transformed cart item object with sanitized and structured data,
 * or null if the input is null or not an object.
 */
function transformCartItem(originalItem) {
    // Check for null or non-object input
    if (typeof originalItem !== 'object' || originalItem === null) return null;

    var transformedItem = {
        uuid: originalItem.uuid || '',
        id: originalItem.id || '',
        productName: originalItem.productName || '',
        productType: originalItem.productType || '',
        brand: originalItem.brand || '',
        isCSCProduct: !!originalItem.isCSCProduct, // Ensures a boolean value
        price: originalItem.price ? {
            sales: originalItem.price.sales ? {
                value: originalItem.price.sales.value || 0,
                currency: originalItem.price.sales.currency || '',
                formatted: originalItem.price.sales.formatted || '',
                decimalPrice: originalItem.price.sales.decimalPrice || ''
            } : {},
            list: originalItem.price.list || null,
            isDefaultPrice: !!originalItem.price.isDefaultPrice // Ensures a boolean value
        } : {},
        quantity: originalItem.quantity || 0,
        UUID: originalItem.UUID || '',
        shipmentUUID: originalItem.shipmentUUID || '',
        isBonusProductLineItem: !!originalItem.isBonusProductLineItem, // Ensures a boolean value
        priceTotal: {
            price: originalItem.priceTotal && originalItem.priceTotal.price ? originalItem.priceTotal.price : '',
        },
        bonusProductLineItemUUID: originalItem.bonusProductLineItemUUID || null,
        preOrderUUID: originalItem.preOrderUUID || null,
        discountLineItems: Array.isArray(originalItem.discountLineItems) ? originalItem.discountLineItems : [],
        link: originalItem.link || '',
        gtmInfo: typeof originalItem.gtmInfo === 'object' ? originalItem.gtmInfo : {},
        master: originalItem.master ? {
            pid: originalItem.master.pid || ''
        } : {},
    };

    if (originalItem.productType === 'variant' || originalItem.productType === 'standard') {
        transformedItem.variationAttributes = Array.isArray(originalItem.variationAttributes) ? originalItem.variationAttributes : [];
        transformedItem.productTypeVariant = originalItem.productTypeVariant || '';
        transformedItem.productSize = originalItem.productSize || '';
        transformedItem.brandTitle = originalItem.brandTitle || '';
        transformedItem.featureSection = Array.isArray(originalItem.featureSection) ? originalItem.featureSection : [];
    }

    if (originalItem.productType === 'bundle') {
        transformedItem.bundledProductLineItems = originalItem.bundledProductLineItems ? originalItem.bundledProductLineItems.map(function(bundledItem) {
            return {
                uuid: bundledItem.uuid || '',
                id: bundledItem.id || '',
                productName: bundledItem.productName || '',
                productType: bundledItem.productType || '',
                brand: bundledItem.brand || '',
                isCSCProduct: !!bundledItem.isCSCProduct,
                price: bundledItem.price ? {
                    sales: bundledItem.price.sales ? {
                        value: bundledItem.price.sales.value || 0,
                        currency: bundledItem.price.sales.currency || '',
                        formatted: bundledItem.price.sales.formatted || '',
                        decimalPrice: bundledItem.price.sales.decimalPrice || ''
                    } : {},
                    list: bundledItem.price.list || null,
                    isDefaultPrice: !!bundledItem.price.isDefaultPrice
                } : {},
                quantity: bundledItem.quantity || 0,
                link: bundledItem.link || '',
                // Only include attributes that match the main item structure
                // Specific bundled item attributes like images, availability, etc., are excluded based on the initial guidance
            };
        }) : [];
        transformedItem.totalBundledItemsQty = Number.isFinite(originalItem.totalBundledItemsQty) ? originalItem.totalBundledItemsQty : 0;
        transformedItem.productTypeBundle = originalItem.productTypeBundle || '';
    }

    return transformedItem;
}


/**
 * @description Inits GTM callback
 */
function init() {
    if (currentCurrency === '') {
        currentCurrency = dataLayer.currencyCode;
        delete dataLayer.currencyCode;
    }

    reportPageView();

    reportGtmImpressions();

    if (window.gtmDelayedEvents) {
        window.gtmDelayedEvents.forEach((delayedEvent) => {
            report(delayedEvent);
        })
        delete window.gtmDelayedEvents;
    }
    // promo impression & interaction
    const promoImpressions = Array.from(document.querySelectorAll('[data-selector-id="promo-impression"]')).map(impressionEl => {
        try {
            const promoDetails = JSON.parse(/** @type {HTMLElement} */(impressionEl).dataset.promoDetails);
            impressionEl.addEventListener('click', () => {
                report({
                    event: 'promotionClick',
                    ecommerce: {
                        promoClick: {
                            promotions: [promoDetails]
                        }
                    }
                });
                if (impressionEl.dataset.isPdp) {
                    report({
                        event: 'pdpPromotion',
                        promoTitle: promoDetails.name || ''
                    });
                }
            }, { passive: true, once: true });
            return promoDetails;
        } catch (e) {
            setTimeout(() => {
                throw e;
            }, 0);
            return false;
        }
    }).filter(Boolean);

    if (promoImpressions.length) {
        report({
            event: 'promotionView',
            ecommerce: {
                promoView: {
                    promotions: promoImpressions
                }
            }
        });
    }

    window.onbeforeunload = sendAbandonmentForm;
}

function reportPageView() {
    let pageViewDataElement = document.getElementById('pageViewData');

    if (pageViewDataElement) {
        try {
            let data = JSON.parse(pageViewDataElement.dataset.gtm);
            const url = window.location.href;
            const urlParams = getUrlParams(url);
            data.referral = urlParams.utm_source || '';

            // Define new parameters for SFMC Collect Code
            const parameters = [
                'sfmc_activity_id', 'sfmc_activity_name', 'sfmc_activityid',
                'sfmc_asset_id', 'sfmc_channel', 'sfmc_id', 'sfmc_j',
                'sfmc_jb', 'sfmc_journey_id', 'sfmc_journey_name', 'sfmc_l',
                'sfmc_mid', 'sfmc_s', 'sfmc_u', 'utm_campaign', 'utm_content',
                'utm_id', 'utm_medium', 'utm_source', 'utm_term'
            ];

            parameters.forEach(param => {
                if (urlParams[param]) {
                    sessionStorage.setItem(param, urlParams[param]);
                }

                // Ensure to pull from sessionStorage if not found in URL parameters
                data[param] = urlParams[param] || sessionStorage.getItem(param) || '';
            });

            data.queryString = url.split('?')[1] || '';
            data.pageName = document.title;
            data.pagePath = window.location.pathname;

            report(data);
        }
        catch (e) { }
    }
}

/**
 * @description Returns wishlist products impression data
 * @param {Array} products - products data
 * @returns {Array} result
 */
function getWishlistImpressions(products) {
    const impressions = [];

    if (!products || !products.length) {
        return impressions;
    }

    for (let index = 0; index < products.length; index++) {
        const product = products[index];

        if (product && !hasBeenImpressed(product.id)) {
            impressions.push({
                list: 'Wishlist',
                position: product.position,
                ...product.gtmInfo
            });
        }
    }

    if (!impressions.length) {
        return impressions;
    }

    return impressions;
}

/**
 * @description Returns searchbox products impression data
 * @param {Array} products - products data
 * @returns {Array} result
 */
function getSearchboxImpressions(products) {
    const impressions = [];

    if (!products || !products.length) {
        return impressions;
    }

    for (let index = 0; index < products.length; index++) {
        const product = products[index];

        if (product) {
            impressions.push({
                list: 'Search Suggestions',
                position: index + 1,
                ...product.gtmInfo
            });
        }
    }

    return impressions;
}

/**
 * @description Gets product list value
 * @param {refElement} productTile - product tile
 * @returns {string} result
 */
function getProductList(productTile) {
    let list = window.location.search.indexOf('q=') > -1 ? 'Search Results' : 'Products Listings';
    const itemList = productTile.data('list');
    const productsGrid = productTile.get().closest('[data-ref="grid"]');
    const categoryPath = productsGrid ? productsGrid.dataset.categoryPath : '';

    if (productTile.data('recommendation')) {
        return 'Products Carousel';
    }

    if (categoryPath !== 'null' && categoryPath !== '') {
        return categoryPath;
    }

    switch (itemList) {
        case 'cart':
            list = 'Shopping Cart';
            break;
        case 'minicart':
            list = 'Mini cart';
            break;
        case 'wishlistProducts':
            list = 'Wishlist';
            break;
        case 'suggestions':
            list = 'Search Suggestions';
            break;
        case 'recentlyViewedCarousel':
            list = 'Recently Viewed Carousel';
            break;
        case 'recommendedCarousel':
            list = 'Recommended Carousel';
            break;
        default:
            break;
    }

    return list;
}

let impressionsDebounce = [];
const reportImpression = debounce(() => {
    impressionsDebounce.forEach(impression => {
        impressedProducts.push(impression.productID || impression.id);

        delete impression.productID;
    });

    const chunks = arrayIntoChunks(impressionsDebounce);

    impressionsDebounce = [];

    chunks.forEach(impressionSet => report({
        event: 'productImpressions',
        ecommerce: {
            currencyCode: currentCurrency,
            impressions: impressionSet.map(impression => {
                return {
                    list: window.location.search.indexOf('q=') > -1 ? 'Search Results' : 'Products Listings',
                    ...impression
                };
            })
        }
    }));
}, 50);

/**
 * @description Sends GTM 404 error event
*/

eventBus.on('gtm.error404', () => {
    report({
        event: 'error404'
    });
});

/**
 * @description Sends GTM event, when customer clicked on social link
 * @param {string} socialChannel - social link name
*/

eventBus.on('gtm.social.click', (widget) => {
    report({
        event: 'socialClick',
        socialChannel: widget.ref('self').get().title
    });
});

eventBus.on('gtm.click.carousel.content', data => {
    report({
        event: 'carouselContentClick',
        carouselName: data.carouselName,
        carouselItemName: data.name,
        carouselItemDate: data.meta.creationDate,
        carouselItemPosition: data.carouselItemPosition

    });
});

eventBus.on('gtm.content.click', element => {
    const contentInfo = element.config.contentInfo;
    const contentButton = element.ref('self').get().querySelector('a');
    contentInfo.contentSlotID = element.config.slotId;

    if (contentButton) {
        contentInfo.destinationUrl = contentButton.href;
        contentInfo.contentButtonID = contentButton.text.trim();
    }

    if (event.target.hasAttribute('href') && contentInfo.contentButtonID) {
        report({
            event: 'button-click',
            buttonName: contentInfo.contentButtonID

        });
    }

    report({
        event: 'moduleClick',
        contentAsset: contentInfo.ID,
        contentSlotID: contentInfo.contentSlotID,
        contentButtonID: contentInfo.contentButtonID || '',
        destinationUrl: contentInfo.destinationUrl || ''

    });
});

eventBus.on('gtm.home.page.button.click', buttonName => {
    report({
        event: 'button-click',
        buttonName: buttonName

    });
});

eventBus.on('gtm.blog.image.click', data => {
    report({
        event: 'articleImage',
        articleImageName: data.articleImageName,
        articleImageDescription: data.articleImageDescription

    });
});

/**
 * @description Sends GTM event, when customer clicked on button
 * @param {string} buttonName - button name
*/

eventBus.on('gtm.button.click', () => {
    report({
        event: 'button-click',
        buttonName: event.target.dataset.name || event.target.innerText.trim()
    });
});

/**
 * @description Sends GTM event, when customer clicked on 'show more' description button on PDP
 * @param {string} buttonName - button name
*/

eventBus.on('gtm.pdp.description.more.click', () => {
    report({
        event: 'pdpAboutMore'
    });
});

/**
 * @description Sends GTM event, when customer clicked on 'show more' attributes button on PDP
 * @param {string} buttonName - button name
*/

eventBus.on('gtm.pdp.attributes.more.click', () => {
    report({
        event: 'pdpAttributesMore'
    });
});

/**
 * @description Sends GTM event, when customer clicked on variation on PDP
 * @param {string} buttonName - button name
*/

eventBus.on('gtm.variation.click', (data) => {
    report({
        event: 'pdpVariation',
        variation: data.variation
    });
});

/**
 * @description Sends GTM event, when customer submit gate window
 * @param {string} customerId - customer id
*/

eventBus.on('gtm.age.gate.enter', customerId => {
    report({
        event: 'ageGateEnter',
        customerId: customerId
    });
});

/**
 * @description Sends GTM event, when customer submit gate window without tracking
*/

eventBus.on('gtm.age.gate.notrack', () => {
    report({
        event: 'ageGateNoTrack'
    });
});

/**
 * @description Sends GTM event, when customer opens gate window
 * @param {string} customerId - customer id
*/

eventBus.on('gtm.age.gate.shown', customerId => {
    report({
        event: 'ageGateShown',
        customerId: customerId
    });
});

/**
 * @description Sends GTM event, when customer clicks on 'forgot password' button on checkout
*/

eventBus.on('gtm.checkout.forgot.password', () => {
    report({
        event: 'checkoutForgotPassword'
    });
});

/**
 * @description Sends GTM event, when customer clicks on 'continue shopping' button on cart
*/

eventBus.on('gtm.cart.continue.shopping.click', () => {
    report({
        event: 'cartContinueShopping'
    });
});

/**
 * @description Sends GTM event, when customer clicks on 'apply coupon' button on cart
*/

eventBus.on('gtm.cart.promo', (couponCode) => {
    report({
        event: 'cartPromo',
        couponCode: couponCode
    });
});

/**
 * @description Sends GTM event, when customer clicks on 'menu item' in MA
*/

eventBus.on('gtm.account.menu', (emitBusEvent) => {
    report({
        event: 'accountMenu',
        accountMenuElement: emitBusEvent.ref('self').getText().trim()
    });
});

/**
 * @description Sends GTM event, when customer clicks in 'address book' in MA
*/

eventBus.on('gtm.address.book', (element) => {
    let gtmObject = {
        event: 'addressBook'
    }
    if (element instanceof RefElement) {
        gtmObject.addressBookAction = element.getText().trim()
    } else {
        gtmObject.addressBookAction = element.ref('self').getText().trim()
    }

    report(gtmObject);
});

/**
 * @description Sends GTM event, when customer clicks in 'address book' in MA
*/

eventBus.on('gtm.order.history', () => {
    const gtmObject = {
        event: 'orderHistory'
    };

    if (event.target.tagName === 'SELECT') {
        gtmObject.orderHistoryAction = event.target.options[event.target.selectedIndex].innerText.trim()
    } else {
        gtmObject.orderHistoryAction = event.target.innerText.trim()
    }

    report(gtmObject);
});

/**
 * @description Sends GTM event, when customer clicks in 'payments' in MA
*/

eventBus.on('gtm.payment.methods', (emitBusEvent) => {
    report({
        event: 'paymentMethods',
        paymentMethodsAction: emitBusEvent.ref('self').getText().trim()
    });
});

/**
 * @description Sends GTM event, when customer clicks on 'edit' button on checkout
*/

eventBus.on('gtm.checkout.edit', (data) => {
    const gtmObject = {
        event: 'checkoutEdit'
    }

    gtmObject.checkoutSection = data.checkoutSection || data.data('name');

    report(gtmObject);
});

/**
 * @description Sends GTM event, when customer signs in
 * @param {object} data - customer object
*/

eventBus.on('gtm.user.login', data => {
    report({
        event: 'accountLogin',
        loginMethod: data.loginMethod,
        sfPersonAccountId: data.sfPersonAccountId,
        commerceKey: data.commerceKey
    });
});

/**
 * @description Sends GTM event, when customer signs out
 * @param {string} customerId - customer id
*/

eventBus.on('gtm.user.logout', element => {
    const gtmData = element.data('gtm');

    report({
        event: 'accountLogout',
        commerceKey: gtmData.commerceKey,
        sfPersonAccountId: gtmData.sfPersonAccountId

    });

    if (typeof zrl_mi_user_login_failure !== 'undefined' && document.getElementById("loyaltyDisallowedCustomer").value === "false") {
        zrl_mi_user_login_failure();
    }
});

/**
 * @description Sends GTM event, when customer does sort
 * @param {string} sortOption - sort Option
*/

eventBus.on('gtm.list.sort', sortOption => {
    report({
        event: 'listSort',
        list: window.location.search.indexOf('q=') > -1 ? 'Search Results' : 'Products Listings',
        sortOption: sortOption
    });
});

/**
 * @description Sends GTM event, when customer applies filters
 * @param {any} refinementParameterMap - sort Option
*/

eventBus.on('gtm.list.filter', params => {
    /**
     * @description Applied filters Mapping
     * @type {any}
    */
    var filter = {};
    Object.keys(params).forEach((paramKey) => {
        if (paramKey.includes('prefn')) {
            let refinementName = params[paramKey];
            let refinementValue = params[paramKey.replace('prefn', 'prefv')];
            filter[refinementName] = refinementValue;
        } else if (paramKey.includes('pmin') && params.pmin && params.pmax) {
            let refinementName = 'price';
            let refinementValue = params.pmin + ' - ' + params.pmax;
            filter[refinementName] = refinementValue;
        } else if (paramKey.includes('type')) {
            filter.type = params.type;
        }
    });

    report({
        event: 'listFilter',
        list: window.location.search.indexOf('q=') > -1 ? 'Search Results' : 'Products Listings',
        filter: filter
    });
});

/**
 * @description Sends GTM event, when customer changes state
 * @param {string} selectedState - selected state
*/

eventBus.on('shipto.state.changed', selectedState => {
    report({
        event: 'shipStateUpdated',
        stateSelected: selectedState
    });
});

/**
 * @description Sends GTM event, when customer clicked on header area
 * @param {object} element - information about click
*/

eventBus.on('gtm.header.clicked', (element) => {
    const gtmObject = {
        event: 'headerClick'
    }
    const targetElement = element.ref('itemLink').length ? element.ref('itemLink') : element.ref('self');

    gtmObject.headerElementText = targetElement.attr('value') || targetElement.data('name') || targetElement.getText().trim();
    gtmObject.headerLevel = 'Top Level';
    report(gtmObject);
});

/**
 * @description Sends GTM event, when customer clicked on header area
 * @param {object} data - information about click
*/

eventBus.on('gtm.header.navigation.clicked', (element) => {
    const targetElement = element.ref('itemLink').length ? element.ref('itemLink') : element.ref('self');

    report({
        event: 'headerClick',
        headerLevel: 'Second Level',
        headerElementText: targetElement.getText().trim()
    });
});

/**
 * @description Sends GTM event, when customer submits form
 * @param {object} formData - form information
*/
eventBus.on('gtm.form.submit', (formData) => {
    //mark form as submitted
    submittedForms.push(formData.formId);
    let data = formGtmData(formData.formName, formData.formId, formData);
    report(data);
});

/**
 * @description Sends GTM event, when field is invalid
 * @param {object} data - form information
*/
eventBus.on('gtm.form.error', (data) => {
    let gtmData = {
        event: 'form-submit',
        formName: data.formName,
        formAction: 'Validate',
        formStatus: 'Error: Form Validation',
        errorMessage: data.errorMessage
    };

    const formId = specialFromIdNameMapping[data.formName] || null;

    if (formId && specialFormsEventData[formId]) {
        const formEventData = specialFormsEventData[formId];
        gtmData.event = formEventData.event;
        gtmData.formName = formEventData.name;
        gtmData.formAction = formEventData.action;
    }

    report(gtmData);
});

/**
 * @description Sends GTM event, when customer clicked on footer area
 * @param {object} widget - emit bus event widget
*/

eventBus.on('gtm.footer.clicked', (widget) => {
    report({
        event: 'footerClick',
        footerElementText: widget.ref('self').get().text.trim() || widget.ref('self').get().title
    });
});

/**
 * @description Sends GTM event, when error occurred
 * @param {object} e - information about click
*/

eventBus.on('gtm.error.occurred', e => {
    report({
        event: 'error',
        errorText: e.message || e.accessibilityAlert
    });
});

/**
 * @description Sends GTM event, when carousel button clicked
 * @param {object} data - information about click
*/

eventBus.on('gtm.carousel.button.click', data => {
    report({
        event: 'carouselNavigationClick',
        carouselButton: data.carouselButton,
        carouselName: data.carouselName
    });
});

/**
 * @description Sends GTM event, when pdp carousel was clicked
 * @param {object} data - information about click
*/

eventBus.on('gtm.carousel.pdp.click', data => {
    report({
        event: 'pdpImageCarousel',
        imagePosition: data.imagePosition,
        imageName: data.imageName
    });
});

/**
 * @description Sends GTM event, when pdp zoom was clicked
 * @param {object} data - information about click
*/

eventBus.on('gtm.pdp.zoom.click', data => {
    report({
        event: 'pdpImageZoom',
        imagePosition: data.imagePosition,
        imageName: data.imageName
    });
});

eventBus.on('product.tile.init', tile => {
    const cart = tile.get().closest('[data-widget="cartMgr"]');


    // we have case for empty cart, when carousel is rendering but is not visible (hidden by CSS).
    // When it happens we don't need to triger this event
    // This code breaks logic for case, when we have empty cart and carousel is visible
    if (cart) {
        const WIDGET_PROP_NAME = '@@_widget_instance_@@';
        const cartWidget = cart[WIDGET_PROP_NAME];
        const isEmptyCart = cartWidget.cart.numItems === 0;
        if (!isEmptyCart) {
            return;
        }
    }

    if (!tile) {
        return;
    }
    const impression = tile.data('analytics') || {};
    const listId = tile.data('list');
    impression.productID = tile.data('pid');
    impression.list = getProductList(tile);

    if (impression && !hasBeenImpressed(impression.productID || impression.id)) {
        setTilePosition(impression, impression.productID, listId);
        impressionsDebounce.push(impression);
        reportImpression();
    }

    const tileImageLinkRef = tile.get().querySelector('[data-ref="tileImageLink"]');
    const tileLinkRef = tile.get().querySelector('[data-ref="tileLink"]') || tile.get();
    const position = `?position=${impression.position || 1}`;

    if (tileImageLinkRef) tileImageLinkRef.href += position
    if (tileLinkRef) tileLinkRef.href += position;
});

eventBus.on('wishlist.products.init', (products, step) => {
    const impressions = getWishlistImpressions(products);

    if (!impressions.length) {
        return;
    }

    const event = {
        event: 'productImpressions',
        ecommerce: {
            currencyCode: currentCurrency,
            impressions: impressions
        }
    };

    if (step && (step === 'browsing')) {
        event.event = 'productImpressions';
    }

    report(event);
});

eventBus.on('searchbox.products.init', (products) => {
    const impressions = getSearchboxImpressions(products);

    if (!impressions.length) {
        return;
    }

    const event = {
        event: 'productImpressions',
        ecommerce: {
            currencyCode: currentCurrency,
            impressions: impressions
        }
    };

    report(event);
});

/**
 * @description Sends GTM event, when product was added to wishlist
 * @param {object} productData - Product data object containing details about the product
*/

eventBus.on('product.added.to.wishlist', productData => {
    const eventData = {
        event: 'addToWishlist',
        id: productData.id,
        upc: productData.upc,
        name: productData.name,
        brand: productData.brand,
        dimension24: productData.dimension24,
        dimension41: productData.dimension41,
        dimension42: productData.dimension42,
        dimension43: productData.dimension43,
        dimension44: productData.dimension44,
        dimension45: productData.dimension45,
        originalPrice: productData.originalPrice,
        price: productData.price
    };

    if (productData.category) {
        eventData.category = productData.category;
    }

    report(eventData);
});

/**
 * @description Sends GTM event, when user clicks on image on content page
 * @param {string} productName - product name
*/

eventBus.on('gtm.article.image', element => {
    const isBlogPage = element.ref('html').get().querySelector('body').dataset.isBlogPage === 'true';

    if (isBlogPage) {
        let text = null;
        let gtmObject = {
            event: 'articleImage',
            articleImageName: element.ref('imageTile').get().alt || element.ref('imageTile').get().title
        };

        const imageWrapper = element.ref('self').get().parentElement;
        const textWrapper = imageWrapper.nextSibling;
        if (textWrapper) {
            text = textWrapper.querySelector('[data-selector-id="text-box"]');
        }

        if (text) {
            gtmObject.articleImageDescription = text.innerText.trim();
        }
        report(gtmObject);
    }
});

/**
 * @description Sends GTM event, when user scroll blog carousel
 * @param {string} productName - product name
*/

eventBus.on('gtm.scroll.blog.carousel', data => {
    report({
        event: 'articleImageSlider',
        articleImageName: data.articleImageName || '',
        articleImageDescription: data.articleImageDescription || '',
        articleImagePosition: data.articleImagePosition
    });
});

/**
 * @description Sends GTM event, when product was removed from wishlist
 * @param {object} productData - Product data object containing details about the product
*/

eventBus.on('product.removed.from.wishlist', productData => {
    const eventData = {
        event: 'removeFromWishlist',
        id: productData.id,
        upc: productData.upc,
        name: productData.name,
        brand: productData.brand,
        dimension24: productData.dimension24,
        dimension41: productData.dimension41,
        dimension42: productData.dimension42,
        dimension43: productData.dimension43,
        dimension44: productData.dimension44,
        dimension45: productData.dimension45,
        originalPrice: productData.originalPrice,
        price: productData.price
    };

    if (productData.category) {
        eventData.category = productData.category;
    }

    report(eventData);
});

eventBus.on('product.tile.qv.open', qvData => {
    report({
        event: 'productView',
        ecommerce: {
            detail: {
                actionField: { list: 'Product quick view' },
                products: [qvData.gtmInfo]
            }
        }
    });
});

eventBus.on('product.changed.quantity', action => {
    report({
        event: 'changeQuantity',
        changeQuantityAction: action
    });
});

eventBus.on('product.tile.qv.open.edit', editEl => {
    if (!editEl) {
        return;
    }

    const productInfo = editEl.data('analytics');

    if (!productInfo) {
        return;
    }

    report({
        ecommerce: {
            detail: {
                actionField: { list: 'Product quick view' },
                products: [productInfo]
            }
        }
    });
});

eventBus.on('tile.product.link.click', productTile => {
    const productAnalyticsDataElement = new RefElement([productTile.get().closest('[data-widget="productTile"]')]);
    const listId = productAnalyticsDataElement.data('list');
    const list = getProductList(productAnalyticsDataElement);
    const tileInfo = productAnalyticsDataElement.data('analytics');
    const pid = productAnalyticsDataElement.data('pid');

    if (!tileInfo) {
        return;
    }

    setTilePosition(tileInfo, pid, listId);

    report({
        event: 'productClick',
        ecommerce: {
            currencyCode: currentCurrency,
            click: {
                actionField: { list },
                products: [tileInfo]
            }
        }
    });
    sessionStorage.setItem('productListGTM', list);
});

/**
 * @description Tracks in dataLayer an information about minicart product tile link click
 * @event "minicart.product.link.click"
 * @listens "minicart.product.link.click"
 * @returns {void}
 */
eventBus.on('minicart.product.link.click', (productTile, action) => {
    const list = getProductList(productTile);
    sessionStorage.setItem('productListGTM', list);
});


eventBus.on('search.box.product.click', searchBoxWidget => {
    const productAnalyticsDataElement = searchBoxWidget.currentSelected;
    const listId = productAnalyticsDataElement.data('list');
    const list = getProductList(productAnalyticsDataElement);
    const tileInfo = productAnalyticsDataElement.data('analytics');
    const pid = productAnalyticsDataElement.data('pid');

    if (!tileInfo) {
        return;
    }

    setTilePosition(tileInfo, pid, listId);
    sessionStorage.setItem('productListGTM', list);

    report({
        event: 'productClick',
        ecommerce: {
            currencyCode: currentCurrency,
            click: {
                actionField: 'Search Suggestions',
                products: [tileInfo]
            }
        }
    });
});

eventBus.on('product.variation.click', (data, selectedVariation) => {
    const list = getProductList(selectedVariation);
    report({
        event: 'productDetailView',
        ecommerce: {
            currencyCode: currentCurrency,
            detail: {
                actionField: { list },
                products: [data]
            }
        }
    });
});

eventBus.on('gtm.product.detail.view', (widget) => {
    const detail = widget.config.gtmproductdetails;
    const list = sessionStorage.getItem('productListGTM');

    report({
        event: 'productDetailView',
        ecommerce: {
            currencyCode: detail.currencyCode,
            detail: {
                actionField: { list: list || detail.list },
                products: detail.products
            }
        }
    });

    sessionStorage.removeItem('productListGTM');
});

eventBus.on('product.added.to.cart', (cart, widget) => {
    const tileInfo = widget.currentGtmInfo || widget.data('analytics');
    const isProductDetailPage = widget.ref('self').data('widget') === 'productDetail';
    const items = [];

    if (!tileInfo) {
        return;
    }

    if (!tileInfo.quantity) {
        tileInfo.quantity = 1;
    }

    if (tileInfo.position) {
        delete tileInfo.position;
    }

    let data = {};

    if (cart.items.length > 0) {
        for (var i = 0; i < cart.items.length; i++) {
            var item = transformCartItem(cart.items[i]);
            items.push(item);
        }
    }

    if (!isProductDetailPage) {
        const list = getProductList(widget.ref('self'));
        data.actionField = { list: list };
    }

    data.products = [].concat(tileInfo);


    let reportObject = {
        event: 'addToCart',
        ecommerce: {
            currencyCode: currentCurrency,
            add: data
        }
    };

    report(reportObject);

    report({
        event: 'cartUpdated',
        ecommerce: {
            itemsInCart: items
        }
    });
});

/**
 * @description Sends to dataLayer an information about product on product link click
 * @param {refElement} productTile - product link
 * @param {string} action - where click was made
 * @returns {void}
 */
function trackProductClicks(productTile, action) {
    const tileInfo = productTile.data('analytics');
    const list = getProductList(productTile);

    sessionStorage.setItem('productListGTM', list);

    if (!tileInfo) {
        return;
    }

    report({
        event: 'productClicks',
        products: {
            name: tileInfo.name,
            id: tileInfo.id,
            action: action || productTile.data('action') || ''
        }
    });
}

eventBus.on('cart.product.link.click', (productTile, action) => {
    let productAnalyticsDataElement;
    const element = productTile.ref && productTile.ref('self');
    if (element) {
        productAnalyticsDataElement = new RefElement([element.get()]);
        trackProductClicks(productAnalyticsDataElement, action);
    } else {
        trackProductClicks(productTile, action);
    }
});

eventBus.on('wishlist.product.link.click', productTile => {
    const productAnalyticsDataElement = new RefElement([productTile.ref('self').get()]);
    const list = getProductList(productAnalyticsDataElement);
    const tileInfo = productAnalyticsDataElement.data('analytics');
    const pid = productAnalyticsDataElement.data('pid');
    const listId = productAnalyticsDataElement.data('list');

    if (!tileInfo) {
        return;
    }

    sessionStorage.setItem('productListGTM', list);

    setTilePosition(tileInfo, pid, listId);

    report({
        event: 'productClick',
        ecommerce: {
            currencyCode: currentCurrency,
            click: {
                actionField: { list },
                products: [tileInfo]
            }
        }
    });
});

eventBus.on('gtm.cart.updated', cart => {
    const items = [];
    if (cart.items.length > 0) {
        for (var i = 0; i < cart.items.length; i++) {
            var item = transformCartItem(cart.items[i]);
            items.push(item);
        }
    }
    report({
        event: 'cartUpdated',
        ecommerce: {
            itemsInCart:items
        }
    });

    if (!cart.items.length) {
        report({
            event: 'cartEmptied'
        });
    }
});

eventBus.on('minicart.updated', cart => {
    const items = [];
    if (cart.items.length > 0) {
        for (var i = 0; i < cart.items.length; i++) {
            var item = transformCartItem(cart.items[i]);
            items.push(item);
        }
    }
    report({
        event: 'cartUpdated',
        ecommerce: {
            itemsInCart:items
        }
    });

    if (!cart.items.length) {
        report({
            event: 'cartEmptied'
        });
    }
});

eventBus.on('cart.remove.product', cart => {
    if (!cart.removeButton) {
        return;
    }

    const tileInfo = cart.removeButton.data('analytics');

    if (!tileInfo) {
        return;
    }

    tileInfo.quantity = parseInt(cart.removeButton.data('quantity'), 10) || 1;

    delete tileInfo.list;

    report({
        event: 'removeFromCart',
        ecommerce: {
            currencyCode: currentCurrency,
            remove: {
                products: [tileInfo]
            }
        }
    });
});

eventBus.on('minicart.remove.product', minicart => {
    if (!minicart.removeProductLink) {
        return;
    }

    const tileInfo = minicart.removeProductLink.data('analytics');

    if (!tileInfo) {
        return;
    }

    tileInfo.quantity = parseInt(minicart.removeProductLink.data('quantity'), 10) || 1;

    report({
        event: 'removeFromCart',
        ecommerce: {
            currencyCode: currentCurrency,
            remove: {
                products: [tileInfo]
            }
        }
    });
});

let shippingMethodName = '';

eventBus.on('checkout.shipping.step.submitted', checkoutMgr => {
    if (checkoutMgr.order && checkoutMgr.order.gtmInfo) {
        shippingMethodName = get(checkoutMgr, 'order.shipping.0.selectedShippingMethod.displayName');
        if (!shippingMethodName) {
            const applicableShippingMethods = get(checkoutMgr, 'order.shipping.0.applicableShippingMethods') || [];
            applicableShippingMethods.forEach(applicableShippingMethod => {
                if (applicableShippingMethod && applicableShippingMethod.selected) {
                    shippingMethodName = applicableShippingMethod.displayName;
                }
            });
        }

        report({
            event: 'checkoutOption',
            ecommerce: {
                checkout_option: {
                    actionField: {
                        step: 2,
                        option: shippingMethodName
                    }
                }
            }
        });
    }
});

eventBus.on('gtm.checkout.billing.step.submitted', checkoutMgr => {
    if (checkoutMgr.order && checkoutMgr.order.gtmInfo) {
        let paymentMethod = get(checkoutMgr, 'order.billing.payment.selectedPaymentInstruments.0.name');

        if (!paymentMethod) {
            paymentMethod = get(checkoutMgr, 'order.billing.payment.applicablePaymentMethods.0.name') || '';
        }

        report({
            event: 'checkoutOption',
            ecommerce: {
                checkout_option: {
                    actionField: {
                        step: 3,
                        option: paymentMethod
                    }
                }
            }
        });
    }
});

function sendFirstStepDataForKnownUser(products, isCustomerFromLoginPage) {
    const actionFieldObject = {
        step: 1
    }
    if (!isCustomerFromLoginPage) {
        actionFieldObject.option = 'logged in';
    }

    report({
        event: 'checkout',
        ecommerce: {
            currencyCode: currentCurrency,
            checkout: {
                actionField: actionFieldObject,
                products: products
            }
        }
    });
}

function sendLoginOption(loginOption) {
    report({
        event: 'checkoutOption',
        ecommerce: {
            checkout_option: {
                actionField: {
                    step: 1,
                    option: loginOption
                }
            }
        }
    });
}

eventBus.on('checkout.selected.shipping.step', () => {
    report({
        event: 'checkout',
        ecommerce: {
            checkout: {
                actionField: {
                    step: 2
                }
            }
        }
    });
});

eventBus.on('checkout.selected.billing.step', () => {
    report({
        event: 'checkout',
        ecommerce: {
            checkout: {
                actionField: {
                    step: 3
                }
            }
        }
    });
});

eventBus.on('checkout.selected.summary.step', () => {
    report({
        event: 'checkout',
        ecommerce: {
            checkout: {
                actionField: {
                    step: 4
                }
            }
        }
    });
});

eventBus.on('gtm.where.to.buy.online', (emitBusEvent) => {
    report({
        event: 'whereToBuyOnline',
        onlineStoreName: emitBusEvent.data('storeName')
    });
});

eventBus.on('gtm.subscribe.success', () => {
    report({
        event: "emailSubscribe",
        formName: "Newsletter",
        formAction: "Opt-In",
        formStatus: "Success"
    });
});

$(document).on('change','#dwfrm_billing_subscribe' ,function(){
    if(this.checked) {
        var gtmData = {
            event: "emailSubscribe",
            formName: "Newsletter",
            formAction: "Opt-In",
            formStatus: "Success",
            "fieldEngagement" : "dwfrm_billing_subscribe"

        };
        report(gtmData);
    }
})

eventBus.on('gtm.checkout.send.first.step', (customer, products, isCustomerFromLoginPage = false) => {
    // sends fist step for registered/guest user
    if (customer.registeredUser) {
        sendFirstStepDataForKnownUser(products, isCustomerFromLoginPage);
        if (!isCustomerFromLoginPage) {
            return;
        }
        if (customer.isExternallyAuthenticated) {
            // TODO:HARMONY:BE pass authMethod
            // sendLoginOption(authMethod)
        }
    } else {
        sendLoginOption('guest');
    }
});

eventBus.on('interacted.field', handleFormInteractedField);

function handleFormInteractedField(interactedData) {
    //mark form as interacted
    handleFormInteraction(interactedData.formName, interactedData.formId);

    //set field interaction data
    interactedForms[interactedData.formId].fields.push(interactedData.fieldName);
}

/**
 * @description Returns specific event name for Form first Interacation
 * @param {string} formId
 * @returns {string}
*/

function getSpecificEventForFirstInteraction(formId) {
    if (formId === 'contactUsForm') {
        return 'contactUsInitiated';
    } else if (formId === 'registerForm') {
        return 'createAccountInitiated';
    }

    return '';
}

/**
 * @description Get contact type
 * @returns {string}
*/
function getContactType() {
    const reasonCode = document.getElementById('reasonCode');
    return reasonCode ? reasonCode.value : '';
}

/**
 * @description Get information from findNearMe form
 * @returns {object}
*/
function getFindNearMeData() {
    let findNearMeData = {};
    const zipCode = document.getElementById('zipCode');
    const radius = document.getElementById('radius');
    const flavor = document.getElementById('flavor');
    const inStore = document.getElementById('inStores');
    const diningOut = document.getElementById('diningOut');
   
    if (zipCode) {
        findNearMeData.zipCode = zipCode.value;
    }

    if (radius) {
        findNearMeData.milesToSearch = radius.value;
    }

    if (flavor) {
        findNearMeData.selectedVarietal = flavor.options[flavor.selectedIndex].text;
    }

    findNearMeData.selectedProduct = findNearMeData.selectedVarietal;
    if (inStore.checked) {
        findNearMeData.selectedType = 'In Store';
    } else if (diningOut.checked) {
        findNearMeData.selectedType = 'Dining Out';
    } else {
        findNearMeData.selectedType = 'Offline';
    }
    
    return findNearMeData;
}

/**
 * @description Handle Form Interacation and mark form as interacted
 * @param {string} formName The name of the form with which the client interacts
 * @param {string} formId
 * @returns {void}
*/
function handleFormInteraction(formName, formId) {
    // send data that form was interacted
    if (!interactedForms[formId]) {
        //generate gtmDataObject
        let data = formGtmData(formName, formId, null);

        if (data.formStatus === 'First Interaction') {
            data.event = getSpecificEventForFirstInteraction(formId) || data.event;
        }

        //report submit event
        report(data);
        //mark as interacted
        interactedForms[formId] = {
            fields: [],
            formName: formName
        };

    }
}

/**
 * @description Prepare GTM data object
 * @param {string} formName The name of the form with which the client interacts
 * @param {string} formId The name of the form widget with which the client interacts
 * @param {any} data additional data
 * @returns {object} return GTM data object
 */
function formGtmData(formName, formId, data) {
    /**
     * @description gtm Object
     * @type {any}
    */
    let gtmObject = {
        formStatus: 'First Interaction'
    };

    if (data) {
        Object.assign(gtmObject, data);
    }

    // special handling for special forms
    // TODO:HARMONY:BE handle in a better way
    if (formId && specialFormsEventData[formId]) {
        const formEventData = specialFormsEventData[formId];
        gtmObject.event = formEventData.event;
        gtmObject.formName = formEventData.name;
        gtmObject.formAction = formEventData.action;
        if (formId === 'contactUsForm') {
            gtmObject.contactTypeSelected = getContactType();
        } else if (formId === 'wheretobuyForm' && gtmObject.formStatus === 'Success') {
            const findNearMeData = getFindNearMeData();
            Object.assign(gtmObject, findNearMeData);
        }
    } else {
        gtmObject.event = specialFormsEventData['default-form'].event;
        gtmObject.formName = formName;
        gtmObject.formAction = specialFormsEventData['default-form'].action;
    }

    return gtmObject;
}

/**
 * @description interacted forms object
 * @type {any}
*/
var interactedForms = {};

/**
 * @description interacted forms array
 * @type {any}
*/
var submittedForms = [];

/**
 * @description Sends Abandonment GTM Event, in case, when user iteracted with form, form wasn't submitted and user has left page
 * @returns {void}
*/
function sendAbandonmentForm() {
    const interactedFormsKeys = Object.keys(interactedForms);
    interactedFormsKeys.forEach(formId => {
        if (submittedForms.indexOf(formId) === -1) {
            /**
             * @description gtm Object
             * @type {any}
            */
            let gtmObject = formGtmData(interactedForms[formId].formName, formId, null);
            gtmObject.formStatus = 'Abandonment';
            gtmObject.fieldEngagement = interactedForms[formId].fields.join(' > ');
            report(gtmObject);
        }
    });
}

/**
 * @description event data for exceptional forms
 * @type {any}
*/
var specialFormsEventData = {
    emailSubscribe: {
        name: 'Newsletter',
        event: 'emailSubscribe',
        action: 'Opt-In'
    },
    registerForm: {
        event: 'createAccount',
        name: 'Create Account',
        action: 'Submit',
        cutomerId: ''
    },
    confirmationCreateAccount: {
        event: 'createAccount',
        name: 'Create Account',
        action: 'Submit',
        cutomerId: ''
    },
    contactUsForm: {
        event: 'contactUs',
        name: 'Contact Us',
        action: 'Submit'
    },
    wheretobuyForm: {
        event: 'findStoreSubmit',
        name: 'Find Store',
        action: 'Submit'
    },
    'default-form': {
        event: 'form-submit',
        action: 'Submit'

    }
};

/**
 * @description map form name to form ID
 * @type {any}
 */
var specialFromIdNameMapping = {
    'subscribe-form': 'emailSubscribe',
    'contactUsForm': 'contactUsForm'
};

if (window.dataLayer) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            setTimeout(() => {
                init();
            }, 0);
        }, { once: true });
    } else {
        init();
    }
}

// store locator 3rd party events
document.addEventListener('gtm.store.list', function (event) {
    const storeData = parseW2BInfor(event.detail.storeData);

    report({
        event: 'whereToBuyMap',
        storeName: storeData.storeName,
        storeAddress: storeData.storeAddress,
        storeCity: storeData.storeCity,
        storeState: storeData.storeState,
        storePosition: event.detail.position
    });
});

function parseW2BInfor(storeInformationArray) {
    const gtmObject = {
        storeName: storeInformationArray[0],
        storeAddress: storeInformationArray[1],
        storeCity: storeInformationArray[2].split(',')[0],
        storeState: storeInformationArray[2].split(',')[1].trim().split(' ')[0],
    };

    return gtmObject;
}
